import React, { useState} from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import API_ENDPOINTS from "../../apis/endpoints";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    AreaChart,
    Area,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

const formatNumber = (num) => {
    if (num === "--" || num === null || num === undefined) return "--";
    if (num >= 1000000000) return (num / 1000000000).toFixed(1) + "B";
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num.toString();
};


const SLICE_CONFIG = {
    last_7_days: 3,
    last_30_days: 6,
    last_90_days: 9,
    last_365_days: 12,
    all_time: Infinity,
};

const getRangeSlice = (series, rangeKey) => {
    if (!Array.isArray(series)) return series;
    const sliceLength = SLICE_CONFIG[rangeKey] || series.length;
    if (!Number.isFinite(sliceLength) || sliceLength >= series.length) return series;
    return series.slice(series.length - sliceLength);
};

// Placeholder: in the future this can scope by actual content type (videos vs shorts)
const getContentTypeSlice = (series, contentType) => {
    // For now, return the full series regardless of type.
    // When API supports per-format breakdown, filter/split here.
    return series;
};

// Transform API response to component structure
const transformApiData = (apiData) => {
    if (!apiData) return null;

    // The fetcher returns data?.data, which gives us { meta: {...}, data: {...} }
    // So we need to access apiData.data to get the actual account data
    // Audience data is now in analytics.insights
    const accountData = apiData.data || apiData;
    const profile = accountData.profile || {};
    const engagement = accountData.engagement || {};
    const analytics = accountData.analytics || {};
    // Access insights from analytics.insights for audience data
    const insights = analytics.insights || [];
    const latestInsights = insights.length > 0 ? insights[insights.length - 1] : null;
    const metrics = latestInsights?.metrics || [];
    // Demographics is at accountData level, not inside profile
    const demographics = accountData.demographics || profile.demographics || {};

    // Transform statistics to chart format
    const transformStatistics = (stats, valueKey = 'followers') => {
        if (!Array.isArray(stats) || stats.length === 0) return [];
        return stats.map(stat => {
            const date = new Date(stat.date);
            return {
                month: date.toLocaleDateString('en-US', { month: 'short' }),
                value: stat[valueKey] || 0,
            };
        });
    };

    // Helper function to get metric value by name
    const getMetricValue = (metricName, asPercentage = false) => {
        const metric = metrics.find(m => m.name === metricName);
        if (!metric) return 0;
        return asPercentage ? (metric.value * 100) : metric.value;
    };

    // Calculate engagement rate: (Likes + Comments + Shares) ÷ (Engaged views)
    const calculateER = () => {
        const likes = getMetricValue('likes');
        const comments = getMetricValue('comments');
        const shares = getMetricValue('shares');
        const engagedViews = getMetricValue('engaged_views');
        
        if (!engagedViews || engagedViews === 0) return 0;
        
        const engagementRate = ((likes + comments + shares) / engagedViews) * 100;
        return engagementRate.toFixed(2);
    };

    // Transform age distribution from insights metrics
    const transformAgeDistribution = () => {
        const ageGroups = [
            { key: 'aud_age_dist_13_17', label: '13-17', value: getMetricValue('aud_age_dist_13_17', true) },
            { key: 'aud_age_dist_18_24', label: '18-24', value: getMetricValue('aud_age_dist_18_24', true) },
            { key: 'aud_age_dist_25_34', label: '25-34', value: getMetricValue('aud_age_dist_25_34', true) },
            { key: 'aud_age_dist_35_44', label: '35-44', value: getMetricValue('aud_age_dist_35_44', true) },
            { key: 'aud_age_dist_45_54', label: '45-54', value: getMetricValue('aud_age_dist_45_54', true) },
            { key: 'aud_age_dist_55_64', label: '55-64', value: getMetricValue('aud_age_dist_55_64', true) },
            { key: 'aud_age_dist_65_plus', label: '65+', value: getMetricValue('aud_age_dist_65_plus', true) },
        ];

        // Find top age group
        const topAgeGroup = ageGroups.reduce((max, group) => 
            group.value > max.value ? group : max, ageGroups[0]
        );

        return {
            ageGroups,
            topAgeGroup: topAgeGroup.value > 0 ? topAgeGroup.label : null,
        };
    };

    // Transform audience gender distribution from insights metrics
    const transformGenderDistribution = () => {
        const female = getMetricValue('aud_gender_dist_female', true);
        const male = getMetricValue('aud_gender_dist_male', true);
        const userSpecified = getMetricValue('aud_gender_dist_user_specified', true);
        
        // Find top gender
        let topGender = null;
        if (male > female && male > userSpecified) {
            topGender = 'Male';
        } else if (female > male && female > userSpecified) {
            topGender = 'Female';
        } else if (userSpecified > 0) {
            topGender = 'User Specified';
        }

        return { 
            male: parseFloat(male.toFixed(2)), 
            female: parseFloat(female.toFixed(2)),
            userSpecified: parseFloat(userSpecified.toFixed(2)),
            topGender
        };
    };

    // Transform demographics for chart (age distribution)
    const transformDemographics = () => {
        const ageDist = transformAgeDistribution();
        // Return age groups with total percentage per age group
        return ageDist.ageGroups.map(group => ({
            ageGroup: group.label,
            male: 0, // Not available in insights
            female: 0, // Not available in insights
            total: parseFloat(group.value.toFixed(2))
        })).filter(group => group.total > 0);
    };

    // Extract email from description
    const extractEmail = (description) => {
        if (!description) return null;
        // Try multiple patterns to extract email
        const patterns = [
            /Email[:\s-]+\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i,
            /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/i
        ];
        
        for (const pattern of patterns) {
            const emailMatch = description.match(pattern);
            if (emailMatch) {
                return emailMatch[1].trim();
            }
        }
        return null;
    };

    // Parse keywords from demographics and convert to hashtags
    const parseKeywordsToHashtags = (keywordsString) => {
        if (!keywordsString || typeof keywordsString !== 'string') return [];
        
        // Extract quoted strings from the keywords string
        // Pattern matches: "keyword1" "keyword2" etc.
        const quotedKeywords = keywordsString.match(/"([^"]+)"/g);
        
        if (!quotedKeywords || quotedKeywords.length === 0) return [];
        
        // Convert to hashtags: remove quotes, replace spaces with nothing or keep as is, add #
        return quotedKeywords.map(keyword => {
            // Remove quotes and convert to hashtag format
            const cleanKeyword = keyword.replace(/"/g, '').trim();
            // Replace spaces with nothing for hashtag format
            const hashtag = cleanKeyword.replace(/\s+/g, '');
            return `#${hashtag}`;
        });
    };

    const ageDist = transformAgeDistribution();
    const genderDist = transformGenderDistribution();

    return {
        id: accountData.account_id,
        name: profile.title || null,
        handle: profile.username || null,
        avatar: profile.image_url || 'https://via.placeholder.com/150',
        category: demographics.category || null,
        description: profile.description || null,
        interests: demographics.interests || [],
        demographics: {
            gender: demographics.gender || null,
            ageGroup: demographics.age_range || null,
            location: profile.location?.country || null,
            language: demographics.language || null,
        },
        lastRefreshDate: accountData.last_refreshed_at 
            ? (() => {
                try {
                    const date = new Date(accountData.last_refreshed_at);
                    return date.toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                    });
                } catch (e) {
                    return null;
                }
            })()
            : null,
        contact: {
            email: extractEmail(profile.description),
            social: profile.profile_url ? [{
                label: 'YouTube Channel',
                url: profile.profile_url
            }] : []
        },
        overallEngagement: {
            followers: engagement.followers || 0,
            following: engagement.following || 0,
            er: parseFloat(calculateER()),
            contentCount: engagement.contents || 0,
            avgViews: engagement.views || 0,
        },
        subscriberCount: transformStatistics(analytics.statistics, 'followers'),
        avgLikesData: transformStatistics(analytics.statistics, 'views'),
        videoCount: transformStatistics(analytics.statistics, 'contents'),
        engagementRateHistogram: [],
        topVideos: [],
        recentVideos: [],
        sponsoredVideos: [],
        similarCreators: [],
        audienceData: {
            topSummary: {
                gender: genderDist.topGender,
                ageGroup: ageDist.topAgeGroup,
            },
            demographics: transformDemographics(),
            genderDistribution: genderDist,
            interests: [], // Not available in insights
            location: {
                cities: [], // Not available in insights
                countries: [], // Not available in insights
            },
            language: [], // Not available in insights
            ethnicities: [], // Not available in insights
            audienceType: {
                real: 0,
                suspicious: 0,
                massFollowers: 0,
                influencers: 0,
            },
            brandAffinity: [],
            credibilityScore: 0,
        },
        mentionsData: {
            hashtags: parseKeywordsToHashtags(demographics.keywords) || [],
        },
        insightsMetrics: {
            avgCommentsPerContent: getMetricValue('avg_comments_per_content'),
            avgContentsAddedPlaylist: getMetricValue('avg_contents_added_playlist'),
            avgEngagedViewsPerContent: getMetricValue('avg_engaged_views_per_content'),
            avgEngagedViewsPerViewer: getMetricValue('avg_engaged_views_per_viewer'),
            avgEngagementRatePerContent: getMetricValue('avg_engagement_rate_per_content', true),
            avgLikesPerContent: getMetricValue('avg_likes_per_content'),
            avgReachPerContent: getMetricValue('avg_reach_per_content'),
            avgSharesPerContent: getMetricValue('avg_shares_per_content'),
            avgViewDurationSeconds: getMetricValue('avg_view_duration_seconds'),
            avgViewsPerContent: getMetricValue('avg_views_per_content'),
            avgViewsPerViewer: getMetricValue('avg_views_per_viewer'),
            avgWatchTimePerUserHoursPerContent: getMetricValue('avg_watch_time_per_user_hours_per_content'),
            comments: getMetricValue('comments'),
            contents: getMetricValue('contents'),
            engagedViews: getMetricValue('engaged_views'),
            engagementRate: getMetricValue('engagement_rate', true),
            impressions: getMetricValue('impressions'),
            likes: getMetricValue('likes'),
            shares: getMetricValue('shares'),
            uniqueViewers: getMetricValue('unique_viewers'),
            views: getMetricValue('views'),
            watchTimeHours: getMetricValue('watch_time_hours'),
        },
    };
};

const ChannelInsights = () => {
    const { id } = useParams();
    const [activeTab, setActiveTab] = useState("Engagement");
    const selectedRange = "last_30_days";
    const contentType = "overall";
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [areInterestsExpanded, setAreInterestsExpanded] = useState(false);

    // Fetch account details using useSWR
    const { data, error, isLoading } = useSWR(
        id ? `${API_ENDPOINTS.BRANDCONNECT_ACCOUNTS}${id}/?platform=youtube` : null
    );
    // Fetch content (top/recent videos)
    const { data: contentData } = useSWR(
        id
            ? `${API_ENDPOINTS.BRANDCONNECT_ACCOUNT_CONTENT}${id}/content/?platform=youtube`
            : null
    );

    // Transform API response
    // The fetcher returns data?.data, which gives us { meta: {...}, data: {...} }
    const baseProfile = data ? transformApiData(data) : null;
    const getContentBuckets = (payload) => {
        // API shape: { data: { top: [...], recent: [...] }, ... }
        const dataNode = payload?.data || payload;
        return {
            top: Array.isArray(dataNode?.top) ? dataNode.top : [],
            recent: Array.isArray(dataNode?.recent) ? dataNode.recent : [],
        };
    };

    const transformVideos = (videos) =>
        (videos || []).map((video) => ({
            id: video.content_id,
            thumbnail: video.media?.thumbnail_url,
            type: video.media?.content_type || "VIDEO",
            views: video.engagement?.views || 0,
            likes: video.engagement?.likes || 0,
            comments: video.engagement?.comments || 0,
            title: video.media?.title || "",
            description: video.media?.description || "",
            link: video.media?.url || "",
            publishedAt: video.media?.published_at
                ? new Date(video.media.published_at).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "numeric" }
                  )
                : "N/A",
        }));

    const profile = baseProfile
        ? {
              ...baseProfile,
              topVideos: transformVideos(getContentBuckets(contentData).top),
              recentVideos: transformVideos(getContentBuckets(contentData).recent),
          }
        : null;

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
            </div>
        );
    }

    if (error || !profile) {
        return <div className="text-center py-12">Profile not found</div>;
    }

    const tabs = ["Engagement", "Audience", "Interests"];

    return (
        <div className="flex flex-col lg:flex-row gap-6 bg-gray-50 min-h-screen p-4 sm:p-6">
            {/* Left Sidebar */}
            <div className="w-full lg:w-72 flex-shrink-0">
                <div className="bg-white rounded-xl border border-black p-4 sm:p-6">
                    {/* Profile Header */}
                    <div className="flex items-start gap-3 mb-4">
                        <img
                            src={profile.avatar}
                            alt={profile.name}
                            className="w-16 h-16 rounded-full object-cover"
                        />
                        <div className="flex-1">
                            <h2 className="font-bold text-lg text-gray-900">
                                {profile.name && profile.name !== 'Unknown' ? profile.name : 'No data available'}
                            </h2>
                            <p
                                className="text-sm text-gray-500 truncate"
                                title={profile.handle || 'No data available'}
                            >
                                {profile.handle || 'No data available'}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                                Category: {profile.category && profile.category !== 'General' 
                                    ? profile.category 
                                    : 'No data available'}
                            </p>
                        </div>
                    </div>

                    {/* Description Section */}
                    <div className="mb-6 pt-6">
                        {profile.description ? (
                            <div className="mb-2 pb-4 border-b border-gray-200">
                                <p
                                    className={`text-sm text-gray-700 whitespace-pre-wrap break-words ${
                                        isDescriptionExpanded ? "" : "line-clamp-3"
                                    }`}
                                >
                                    {profile.description}
                                </p>
                                <button
                                    type="button"
                                    className="text-xs text-black hover:underline mt-2"
                                    onClick={() =>
                                        setIsDescriptionExpanded((prev) => !prev)
                                    }
                                >
                                    {isDescriptionExpanded ? "Show Less" : "Show More"}
                                </button>
                            </div>
                        ) : (
                            <p className="text-sm font-semibold text-gray-700 mb-2 pb-4 border-b border-gray-200">
                                No description available
                            </p>
                        )}
                        <div className="mb-3">
                            <p className="text-xs text-gray-500 uppercase font-semibold mb-2">
                                TOP INTERESTS
                            </p>
                            {profile.interests && profile.interests.length > 0 ? (
                                <div>
                                    <div
                                        className={`flex flex-wrap gap-1 ${
                                            areInterestsExpanded ? "" : "line-clamp-3 overflow-hidden"
                                        }`}
                                    >
                                    {profile.interests.map((interest, idx) => (
                                        <span
                                            key={idx}
                                            className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs border border-gray-200"
                                        >
                                            {interest}
                                        </span>
                                    ))}
                                    </div>
                                    <button
                                        type="button"
                                        className="text-xs text-black hover:underline mt-2"
                                        onClick={() =>
                                            setAreInterestsExpanded((prev) => !prev)
                                        }
                                    >
                                        {areInterestsExpanded ? "Show Less" : "Show More"}
                                    </button>
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400 italic">No data available</p>
                            )}
                        </div>
                    </div>

                    {/* Demographics */}
                    <div className="space-y-3 mb-6 p-4 border border-gray-200 rounded-lg bg-white">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 uppercase text-xs font-semibold">
                                GENDER
                            </span>
                            <span className="text-gray-900 font-medium">
                                {profile.demographics.gender && profile.demographics.gender !== '--' 
                                    ? profile.demographics.gender 
                                    : 'No data available'}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 uppercase text-xs font-semibold">
                                AGE GROUP
                            </span>
                            <span className="text-gray-900 font-medium">
                                {profile.demographics.ageGroup && profile.demographics.ageGroup !== '--' 
                                    ? profile.demographics.ageGroup 
                                    : 'No data available'}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 uppercase text-xs font-semibold">
                                LANGUAGE
                            </span>
                            <span className="text-gray-900 font-medium">
                                {profile.demographics.language || 'No data available'}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 uppercase text-xs font-semibold">
                                LOCATION
                            </span>
                            <span className="text-gray-900 font-medium">
                                {profile.demographics.location && profile.demographics.location !== '--' 
                                    ? profile.demographics.location 
                                    : 'No data available'}
                            </span>
                        </div>
                    </div>

                    {/* Profile Info */}
                    {/* <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 uppercase text-xs font-semibold">
                                PROFILE TYPE
                            </span>
                            <span className="text-gray-900 font-medium">
                                {profile.profileType}
                            </span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-500 uppercase text-xs font-semibold">
                                PROFILE STATUS
                            </span>
                            <span className="text-green-600 font-medium flex items-center gap-1">
                                <span className="w-2 h-2 bg-green-600 rounded-full" />
                                {profile.profileStatus}
                            </span>
                        </div>
                    </div> */}

                    {/* Last Refresh */}
                    <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center gap-2 text-sm">
                            <svg
                                className="w-4 h-4 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <div>
                                <p className="text-xs text-gray-500 uppercase font-semibold">
                                    LAST REFRESH DATE
                                </p>
                                <p className="text-gray-900 font-medium">
                                    {profile.lastRefreshDate}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Contact Details */}
                    <div className="p-4 border border-gray-200 rounded-lg bg-white">
                        <div className="flex items-center justify-between mb-3">
                            <p className="text-sm font-semibold text-gray-700">
                                Contact Details
                            </p>
                            {/* <button className="text-gray-400 hover:text-gray-600">
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                    />
                                </svg>
                            </button> */}
                        </div>
                        <div className="space-y-3">
                            {/* Email */}
                            {profile.contact.email && profile.contact.email !== 'N/A' ? (
                                <div className="flex items-start gap-2 text-sm">
                                    <svg
                                        className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                    <span className="text-gray-600 break-words min-w-0 flex-1">
                                        {profile.contact.email}
                                    </span>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 italic">No data available</p>
                            )}

                            {/* Social Links */}
                            {profile.contact.social && profile.contact.social.length > 0 ? (
                                <div className="flex items-start gap-2 text-sm">
                                    <svg
                                        className="w-4 h-4 text-gray-400 mt-0.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13.828 10.172a4 4 0 015.656 5.656l-3.536 3.536a4 4 0 01-5.656-5.656M10.172 13.828a4 4 0 01-5.656-5.656l3.536-3.536a4 4 0 015.656 5.656"
                                        />
                                    </svg>
                                    <div className="flex flex-wrap gap-2">
                                        {profile.contact.social.map((item, idx) => (
                                            <a
                                                key={`${item.label}-${idx}`}
                                                href={item.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                                            >
                                                {item.label}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                !profile.contact.email || profile.contact.email === 'N/A' ? null : (
                                    <p className="text-sm text-gray-400 italic">No social links available</p>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
                <div className="bg-white rounded-xl border border-black">
                    {/* Tabs + Content Type + Date Range */}
                    <div className="border-b border-gray-200 px-6 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex gap-8 overflow-x-auto">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setActiveTab(tab)}
                                    className={`py-4 text-base font-medium border-b-2 transition-colors whitespace-nowrap ${
                                        activeTab === tab
                                            ? "border-gray-900 text-gray-900"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="p-6">
                        {activeTab === "Engagement" && (
                            <div className="space-y-6">
                                {/* Overall Engagement */}
                                <div className="bg-white border border-gray-200 rounded-xl p-4">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                                        Overall Engagement
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <MetricCard
                                            icon="👥"
                                            label="FOLLOWERS"
                                            value={formatNumber(
                                                profile.overallEngagement.followers
                                            )}
                                        />
                                        <MetricCard
                                            icon="📊"
                                            label="ER%"
                                            value={`${profile.overallEngagement.er}%`}
                                        />
                                        <MetricCard
                                            icon="📝"
                                            label="CONTENT COUNT"
                                            value={formatNumber(
                                                profile.overallEngagement
                                                    .contentCount
                                            )}
                                        />
                                        <MetricCard
                                            icon="👁️"
                                            label="VIEWS"
                                            value={formatNumber(
                                                profile.overallEngagement.avgViews
                                            )}
                                        />
                                        <MetricCard
                                            icon="👍"
                                            label="LIKES"
                                            value={formatNumber(
                                                profile.insightsMetrics?.likes || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="💬"
                                            label="COMMENTS"
                                            value={formatNumber(
                                                profile.insightsMetrics?.comments || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="👁️"
                                            label="ENGAGED VIEWS"
                                            value={formatNumber(
                                                profile.insightsMetrics?.engagedViews || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="👤"
                                            label="UNIQUE VIEWERS"
                                            value={formatNumber(
                                                profile.insightsMetrics?.uniqueViewers || 0
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Average Metrics Per Content */}
                                <div className="bg-white border border-gray-200 rounded-xl p-4">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                                        Average Metrics Per Content
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <MetricCard
                                            icon="💬"
                                            label="AVG. COMMENTS/CONTENT"
                                            value={formatNumber(
                                                profile.insightsMetrics?.avgCommentsPerContent || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="📋"
                                            label="AVG. CONTENTS ADDED TO PLAYLIST"
                                            value={formatNumber(
                                                profile.insightsMetrics?.avgContentsAddedPlaylist || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="👁️"
                                            label="AVG. ENGAGED VIEWS/CONTENT"
                                            value={formatNumber(
                                                profile.insightsMetrics?.avgEngagedViewsPerContent || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="👍"
                                            label="AVG. LIKES/CONTENT"
                                            value={formatNumber(
                                                profile.insightsMetrics?.avgLikesPerContent || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="📢"
                                            label="AVG. REACH/CONTENT"
                                            value={formatNumber(
                                                profile.insightsMetrics?.avgReachPerContent || 0
                                            )}
                                        />
                                        <MetricCard
                                            icon="👀"
                                            label="AVG. VIEWS/CONTENT"
                                            value={formatNumber(
                                                profile.insightsMetrics?.avgViewsPerContent || 0
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Charts Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Subscriber Count - Bar Chart with Amber Gradient */}
                                    <ChartCard title="Subscriber Count">
                                        {profile.subscriberCount && profile.subscriberCount.length > 0 ? (
                                            <ResponsiveContainer
                                                width="100%"
                                                height={200}
                                            >
                                                <BarChart
                                                    data={getRangeSlice(
                                                        getContentTypeSlice(
                                                            profile.subscriberCount,
                                                            contentType
                                                        ),
                                                        selectedRange
                                                    )}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="barGradientAmber"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="0%"
                                                                stopColor="#F59E0B"
                                                                stopOpacity={1}
                                                            />
                                                            <stop
                                                                offset="100%"
                                                                stopColor="#FBBF24"
                                                                stopOpacity={0.8}
                                                            />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                        vertical={false}
                                                        stroke="#E5E7EB"
                                                    />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        tickFormatter={formatNumber}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <Tooltip
                                                        formatter={(value) =>
                                                            formatNumber(value)
                                                        }
                                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                        labelStyle={{ color: '#FFFFFF' }}
                                                        itemStyle={{ color: '#FFFFFF' }}
                                                    />
                                                    <Bar
                                                        dataKey="value"
                                                        fill="url(#barGradientAmber)"
                                                        radius={[8, 8, 0, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-[200px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                    {/* View Count - Line Chart */}
                                    <ChartCard title="View Count">
                                        {profile.avgLikesData && profile.avgLikesData.length > 0 ? (
                                            <ResponsiveContainer
                                                width="100%"
                                                height={200}
                                            >
                                                <LineChart
                                                    data={getRangeSlice(
                                                        getContentTypeSlice(
                                                            profile.avgLikesData,
                                                            contentType
                                                        ),
                                                        selectedRange
                                                    )}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="lineGradientRed"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="0%"
                                                                stopColor="#EF4444"
                                                                stopOpacity={1}
                                                            />
                                                            <stop
                                                                offset="100%"
                                                                stopColor="#F87171"
                                                                stopOpacity={0.3}
                                                            />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                        vertical={false}
                                                        stroke="#E5E7EB"
                                                    />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        tickFormatter={formatNumber}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <Tooltip
                                                        formatter={(value) =>
                                                            formatNumber(value)
                                                        }
                                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                        labelStyle={{ color: '#FFFFFF' }}
                                                        itemStyle={{ color: '#FFFFFF' }}
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke="#EF4444"
                                                        strokeWidth={3}
                                                        dot={{ fill: '#EF4444', r: 4 }}
                                                        activeDot={{ r: 6 }}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-[200px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                    {/* Avg. Comments - Area Chart */}
                                    <ChartCard title="Avg. Comments">
                                        {profile.avgLikesData && profile.avgLikesData.length > 0 ? (
                                            <ResponsiveContainer
                                                width="100%"
                                                height={200}
                                            >
                                                <AreaChart
                                                    data={getRangeSlice(
                                                        getContentTypeSlice(
                                                            profile.avgLikesData,
                                                            contentType
                                                        ),
                                                        selectedRange
                                                    )}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="areaGradientTeal"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="0%"
                                                                stopColor="#14B8A6"
                                                                stopOpacity={0.8}
                                                            />
                                                            <stop
                                                                offset="100%"
                                                                stopColor="#5EEAD4"
                                                                stopOpacity={0.2}
                                                            />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid
                                                        strokeDasharray="5 5"
                                                        vertical={false}
                                                        stroke="#D1D5DB"
                                                    />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        tickFormatter={formatNumber}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <Tooltip
                                                        formatter={(value) =>
                                                            formatNumber(value)
                                                        }
                                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                        labelStyle={{ color: '#FFFFFF' }}
                                                        itemStyle={{ color: '#FFFFFF' }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke="#14B8A6"
                                                        strokeWidth={2}
                                                        fill="url(#areaGradientTeal)"
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-[200px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                    {/* Video Count - Bar Chart with Violet Gradient */}
                                    <ChartCard title="Video Count">
                                        {profile.videoCount && profile.videoCount.length > 0 ? (
                                            <ResponsiveContainer
                                                width="100%"
                                                height={200}
                                            >
                                                <BarChart
                                                    data={getRangeSlice(
                                                        getContentTypeSlice(
                                                            profile.videoCount,
                                                            contentType
                                                        ),
                                                        selectedRange
                                                    )}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="barGradientViolet"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="0%"
                                                                stopColor="#7C3AED"
                                                                stopOpacity={1}
                                                            />
                                                            <stop
                                                                offset="100%"
                                                                stopColor="#A78BFA"
                                                                stopOpacity={0.75}
                                                            />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid
                                                        strokeDasharray="4 2"
                                                        vertical={false}
                                                        stroke="#D1D5DB"
                                                    />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        tickFormatter={formatNumber}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <Tooltip
                                                        formatter={(value) =>
                                                            formatNumber(value)
                                                        }
                                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                        labelStyle={{ color: '#FFFFFF' }}
                                                        itemStyle={{ color: '#FFFFFF' }}
                                                    />
                                                    <Bar
                                                        dataKey="value"
                                                        fill="url(#barGradientViolet)"
                                                        radius={[9, 9, 0, 0]}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-[200px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                    {/* Engaged View Count - Line Chart */}
                                    <ChartCard title="Engaged View Count">
                                        {profile.avgLikesData && profile.avgLikesData.length > 0 ? (
                                            <ResponsiveContainer
                                                width="100%"
                                                height={200}
                                            >
                                                <LineChart
                                                    data={getRangeSlice(
                                                        getContentTypeSlice(
                                                            profile.avgLikesData,
                                                            contentType
                                                        ),
                                                        selectedRange
                                                    )}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="lineGradientEmerald"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="0%"
                                                                stopColor="#059669"
                                                                stopOpacity={1}
                                                            />
                                                            <stop
                                                                offset="100%"
                                                                stopColor="#10B981"
                                                                stopOpacity={0.3}
                                                            />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid
                                                        strokeDasharray="2 2"
                                                        vertical={false}
                                                        stroke="#E5E7EB"
                                                    />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        tickFormatter={formatNumber}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <Tooltip
                                                        formatter={(value) =>
                                                            formatNumber(value)
                                                        }
                                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                        labelStyle={{ color: '#FFFFFF' }}
                                                        itemStyle={{ color: '#FFFFFF' }}
                                                    />
                                                    <Line
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke="#059669"
                                                        strokeWidth={2.5}
                                                        dot={{ fill: '#059669', r: 3 }}
                                                        activeDot={{ r: 5 }}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-[200px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                    {/* Unique Viewers - Area Chart */}
                                    <ChartCard title="Unique Viewers">
                                        {profile.avgLikesData && profile.avgLikesData.length > 0 ? (
                                            <ResponsiveContainer
                                                width="100%"
                                                height={200}
                                            >
                                                <AreaChart
                                                    data={getRangeSlice(
                                                        getContentTypeSlice(
                                                            profile.avgLikesData,
                                                            contentType
                                                        ),
                                                        selectedRange
                                                    )}
                                                >
                                                    <defs>
                                                        <linearGradient
                                                            id="areaGradientIndigo"
                                                            x1="0"
                                                            y1="0"
                                                            x2="0"
                                                            y2="1"
                                                        >
                                                            <stop
                                                                offset="0%"
                                                                stopColor="#6366F1"
                                                                stopOpacity={0.8}
                                                            />
                                                            <stop
                                                                offset="100%"
                                                                stopColor="#818CF8"
                                                                stopOpacity={0.2}
                                                            />
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid
                                                        strokeDasharray="4 4"
                                                        vertical={false}
                                                        stroke="#D1D5DB"
                                                    />
                                                    <XAxis
                                                        dataKey="month"
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 11, fill: '#6B7280' }}
                                                        tickFormatter={formatNumber}
                                                        stroke="#9CA3AF"
                                                    />
                                                    <Tooltip
                                                        formatter={(value) =>
                                                            formatNumber(value)
                                                        }
                                                        contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                        labelStyle={{ color: '#FFFFFF' }}
                                                        itemStyle={{ color: '#FFFFFF' }}
                                                    />
                                                    <Area
                                                        type="monotone"
                                                        dataKey="value"
                                                        stroke="#6366F1"
                                                        strokeWidth={2}
                                                        fill="url(#areaGradientIndigo)"
                                                    />
                                                </AreaChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-[200px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                </div>

                                {/* Top Videos */}
                                <VideoCarousel
                                    title="Top videos"
                                    videos={profile.topVideos}
                                />

                                {/* Recent Videos */}
                                <VideoCarousel
                                    title="Recent videos"
                                    videos={profile.recentVideos}
                                />

                            </div>
                        )}

                        {activeTab === "Audience" && profile.audienceData && (
                            <div className="space-y-6">
                                {/* Top Summary Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <SummaryCard
                                        icon="👤"
                                        label="TOP GENDER"
                                        value={profile.audienceData.topSummary.gender || 'No data available'}
                                    />
                                    <SummaryCard
                                        icon="👥"
                                        label="TOP AGE GROUP"
                                        value={profile.audienceData.topSummary.ageGroup || 'No data available'}
                                    />
                                </div>

                                {/* Row 1: Age Distribution and Gender Distribution */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Age Distribution */}
                                    <ChartCard title="Age Distribution">
                                        {profile.audienceData.demographics && profile.audienceData.demographics.length > 0 ? (
                                            <>
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={250}
                                                >
                                                    <BarChart
                                                        data={profile.audienceData.demographics}
                                                    >
                                                        <CartesianGrid
                                                            strokeDasharray="3 3"
                                                            vertical={false}
                                                        />
                                                        <XAxis
                                                            dataKey="ageGroup"
                                                            tick={{ fontSize: 12 }}
                                                        />
                                                        <YAxis
                                                            tick={{ fontSize: 12 }}
                                                        />
                                                        <Tooltip 
                                                            formatter={(value) => `${value.toFixed(2)}%`}
                                                            contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                            labelStyle={{ color: '#FFFFFF' }}
                                                            itemStyle={{ color: '#FFFFFF' }}
                                                        />
                                                        <Bar
                                                            dataKey="total"
                                                            fill="#14B8A6"
                                                            name="Percentage"
                                                            radius={[4, 4, 0, 0]}
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </>
                                        ) : (
                                            <div className="flex items-center justify-center h-[250px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>

                                    {/* Gender Distribution */}
                                    <ChartCard title="Gender distribution">
                                        {(profile.audienceData.genderDistribution.male > 0 || 
                                          profile.audienceData.genderDistribution.female > 0 ||
                                          profile.audienceData.genderDistribution.userSpecified > 0) ? (
                                            <>
                                                <ResponsiveContainer
                                                    width="100%"
                                                    height={250}
                                                >
                                                    <PieChart>
                                                        <Pie
                                                            data={[
                                                                {
                                                                    name: "Male",
                                                                    value: profile.audienceData.genderDistribution.male,
                                                                },
                                                                {
                                                                    name: "Female",
                                                                    value: profile.audienceData.genderDistribution.female,
                                                                },
                                                                ...(profile.audienceData.genderDistribution.userSpecified > 0 ? [{
                                                                    name: "User Specified",
                                                                    value: profile.audienceData.genderDistribution.userSpecified,
                                                                }] : []),
                                                            ]}
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius={60}
                                                            outerRadius={90}
                                                            paddingAngle={2}
                                                            dataKey="value"
                                                        >
                                                            <Cell fill="#14B8A6" />
                                                            <Cell fill="#FB923C" />
                                                            {profile.audienceData.genderDistribution.userSpecified > 0 && (
                                                                <Cell fill="#8B5CF6" />
                                                            )}
                                                        </Pie>
                                                        <Tooltip 
                                                            formatter={(value) => `${value.toFixed(2)}%`}
                                                            contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px', color: '#FFFFFF' }}
                                                            labelStyle={{ color: '#FFFFFF' }}
                                                            itemStyle={{ color: '#FFFFFF' }}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                                <div className="text-center">
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        {profile.audienceData.genderDistribution.male.toFixed(1)}
                                                        %
                                                    </p>
                                                    <div className="flex justify-center gap-4 mt-2">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-3 h-3 bg-[#14B8A6] rounded-full" />
                                                            <span className="text-xs text-gray-600">
                                                                Male {profile.audienceData.genderDistribution.male.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-3 h-3 bg-[#FB923C] rounded-full" />
                                                            <span className="text-xs text-gray-600">
                                                                Female {profile.audienceData.genderDistribution.female.toFixed(1)}%
                                                            </span>
                                                        </div>
                                                        {profile.audienceData.genderDistribution.userSpecified > 0 && (
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-3 h-3 bg-[#8B5CF6] rounded-full" />
                                                                <span className="text-xs text-gray-600">
                                                                    Other {profile.audienceData.genderDistribution.userSpecified.toFixed(1)}%
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex items-center justify-center h-[250px]">
                                                <p className="text-gray-400 italic">No data available</p>
                                            </div>
                                        )}
                                    </ChartCard>
                                </div>


                            </div>
                        )}

                        {activeTab === "Interests" && (
                            <div className="space-y-6">
                                {/* Content Categories */}
                                <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                                        Content Categories
                                        </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {profile.interests.map((interest, idx) => (
                                                <span
                                                    key={idx}
                                                className="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                                                >
                                                {interest}
                                                </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Hashtags & Topics */}
                                <div className="bg-white border border-gray-200 rounded-xl p-6">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                                        Popular Hashtags & Topics
                                    </h3>
                                    {profile.mentionsData && profile.mentionsData.hashtags ? (
                                        <div className="flex flex-wrap gap-3">
                                            {profile.mentionsData.hashtags.map(
                                                (hashtag, idx) => (
                                                    <span
                                                    key={idx}
                                                        className="px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-sm font-medium border border-purple-200"
                                                    >
                                                        {hashtag}
                                                    </span>
                                            )
                                        )}
                                    </div>
                                    ) : (
                                        <p className="text-gray-500 text-sm">No hashtags data available</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab !== "Engagement" &&
                            activeTab !== "Audience" &&
                            activeTab !== "Interests" && (
                                <div className="text-center py-12 text-gray-500">
                                    <p className="text-lg font-medium">
                                    {activeTab} content coming soon...
                                    </p>
                                </div>
                            )}
                    </div>
                </div>
            </div>

        </div>
    );
};

const MetricCard = ({ icon, label, value }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-lg">{icon}</span>
            <p className="text-xs text-gray-500 uppercase font-semibold">
                {label}
            </p>
        </div>
        <p className="text-lg font-bold text-gray-900">{value}</p>
    </div>
);

const ChartCard = ({ title, children }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-bold text-gray-900 mb-4">{title}</h4>
        {children}
    </div>
);

const VideoCarousel = ({ title, videos }) => {
    const [pageIndex, setPageIndex] = useState(0);

    if (!videos || videos.length === 0) {
        return (
            <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                    <p className="text-gray-400 italic">No data available</p>
                </div>
            </div>
        );
    }

    const pageSize = 4;
    const totalPages = Math.max(1, Math.ceil(videos.length / pageSize));
    const pages = Array.from({ length: totalPages }).map((_, idx) =>
        videos.slice(idx * pageSize, idx * pageSize + pageSize)
    );

    const goPrev = () =>
        setPageIndex((prev) => (prev - 1 + totalPages) % totalPages);
    const goNext = () => setPageIndex((prev) => (prev + 1) % totalPages);

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                        {Array.from({ length: totalPages }).map((_, idx) => (
                            <div
                                key={idx}
                                className={`w-2 h-2 rounded-full ${
                                    idx === pageIndex ? "bg-gray-900" : "bg-gray-300"
                                }`}
                            />
                        ))}
                    </div>
                <button
                    type="button"
                    className="p-1 border border-gray-300 rounded-full hover:bg-gray-50"
                    onClick={goPrev}
                >
                    <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </button>
                <button
                    type="button"
                    className="p-1 border border-gray-300 rounded-full hover:bg-gray-50"
                    onClick={goNext}
                >
                    <svg
                        className="w-4 h-4 text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </button>
            </div>
        </div>
        <div className="overflow-hidden">
            <div
                className="flex transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${pageIndex * 100}%)` }}
            >
                {pages.map((page, pageIdx) => (
                    <div
                        key={pageIdx}
                        className="w-full flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4"
                    >
                        {page.map((video) => (
                <div
                    key={video.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                    <div className="relative">
                        <img
                            src={video.thumbnail}
                            alt="Video"
                            className="w-full h-48 object-cover"
                        />
                        <span className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                            {video.type}
                        </span>
                        <div className="absolute bottom-2 left-2 right-2 flex justify-between text-white text-xs">
                            <span className="flex items-center gap-1 bg-black bg-opacity-70 px-2 py-1 rounded">
                                <svg
                                    className="w-3 h-3"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                    <path
                                        fillRule="evenodd"
                                        d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                {formatNumber(video.views)}
                            </span>
                            <span className="flex items-center gap-1 bg-black bg-opacity-70 px-2 py-1 rounded">
                                ❤️ {formatNumber(video.likes)}
                            </span>
                            <span className="flex items-center gap-1 bg-black bg-opacity-70 px-2 py-1 rounded">
                                💬 {formatNumber(video.comments)}
                            </span>
                        </div>
                    </div>
                    <div className="p-3">
                                    <p className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                                        {video.title || "Untitled video"}
                                    </p>
                        <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                                        {video.description || "No description provided."}
                        </p>
                        <div className="flex justify-between items-center text-xs">
                            <a
                                            href={video.link || "#"}
                                            target={video.link ? "_blank" : "_self"}
                                            rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                            >
                                            View
                            </a>
                            <span className="text-gray-500">
                                            Published: {video.publishedAt}
                            </span>
                        </div>
                    </div>
                </div>
            ))}
                    </div>
                ))}
            </div>
        </div>
    </div>
    );
};


const SummaryCard = ({ icon, label, value }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{icon}</span>
            <p className="text-xs text-gray-500 uppercase font-semibold">
                {label}
            </p>
        </div>
        <p className="text-base font-bold text-gray-900">
            {value && value !== '--' ? value : 'No data available'}
        </p>
    </div>
);


export default ChannelInsights;


